import type { GradePointMapping } from '@gpa-scales/shared-types';

/**
 * Create a custom grading scale from user-provided mappings.
 * Validates and normalizes the mappings.
 */
export function createCustomScale(
  mappings: { grade: string; points: number; isPassing?: boolean }[]
): GradePointMapping[] {
  return mappings.map((m) => ({
    grade: m.grade.toUpperCase().trim(),
    points: Math.max(0, Math.min(10, m.points)),
    isPassing: m.isPassing ?? m.points > 0,
  }));
}

/**
 * Validate a custom grading scale.
 * Returns an array of error messages (empty if valid).
 */
export function validateCustomScale(
  mappings: { grade: string; points: number }[]
): string[] {
  const errors: string[] = [];

  if (mappings.length === 0) {
    errors.push('Grading scale must have at least one entry');
    return errors;
  }

  const seen = new Set<string>();
  for (const mapping of mappings) {
    const normalized = mapping.grade.toUpperCase().trim();

    if (!normalized) {
      errors.push('Grade cannot be empty');
      continue;
    }

    if (seen.has(normalized)) {
      errors.push(`Duplicate grade: "${normalized}"`);
    }
    seen.add(normalized);

    if (mapping.points < 0) {
      errors.push(`Points for "${normalized}" cannot be negative`);
    }

    if (mapping.points > 10) {
      errors.push(`Points for "${normalized}" (${mapping.points}) seems unreasonably high`);
    }
  }

  return errors;
}
